import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { Loader2, DollarSign, Building2, Percent, TrendingUp, BedDouble } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useRevenueStatistics, useRoomSoldStatistics } from '@/hooks/api/useReportsDepth';
import { formatMoney } from '@/lib/money';

export function ReportsStatisticsPage() {
  const propertyId = useSelectedPropertyId();
  const [from, setFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const query = { propertyId, from, to };
  const { data: revenue, isLoading: revenueLoading } = useRevenueStatistics(query);
  const { data: roomsSold, isLoading: roomsLoading } = useRoomSoldStatistics(query);

  const maxSold = roomsSold
    ? Math.max(1, ...roomsSold.daily.map((d) => d.sold))
    : 1;

  return (
    <div>
      <PageHeader
        title="Statistics"
        description="Revenue and room-sold performance for a date range"
      />

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label htmlFor="stat-from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input
            id="stat-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="stat-to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input
            id="stat-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Revenue statistics */}
      <section className="mb-8">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Revenue</h3>
        {revenueLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={28} className="animate-spin text-primary-500" aria-label="Loading" />
          </div>
        ) : !revenue ? (
          <EmptyState title="No revenue data" description="No revenue for the selected range." />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={DollarSign}
              value={formatMoney(revenue.totalRevenue)}
              label="Total Revenue"
              borderColor="border-l-green-500"
            />
            <StatCard
              icon={BedDouble}
              value={formatMoney(revenue.roomRevenue)}
              label="Room Revenue"
              borderColor="border-l-blue-500"
            />
            <StatCard
              icon={Building2}
              value={formatMoney(revenue.otherRevenue)}
              label="Other Revenue"
              borderColor="border-l-purple-500"
            />
            <StatCard
              icon={Percent}
              value={`${revenue.occupancyRate}%`}
              label="Occupancy"
              borderColor="border-l-orange-500"
            />
            <StatCard
              icon={TrendingUp}
              value={formatMoney(revenue.adr)}
              label="ADR"
              borderColor="border-l-teal-500"
            />
            <StatCard
              icon={TrendingUp}
              value={formatMoney(revenue.revPar)}
              label="RevPAR"
              borderColor="border-l-pink-500"
            />
          </div>
        )}
      </section>

      {/* Rooms sold statistics */}
      <section>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Rooms Sold</h3>
        {roomsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={28} className="animate-spin text-primary-500" aria-label="Loading" />
          </div>
        ) : !roomsSold ? (
          <EmptyState title="No room-sold data" description="No rooms sold in the selected range." />
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="mb-4">
              <StatCard
                icon={BedDouble}
                value={roomsSold.totalRoomNightsSold}
                label="Total Room-Nights Sold"
                borderColor="border-l-blue-500"
                className="max-w-xs"
              />
            </div>
            {roomsSold.daily.length === 0 ? (
              <p className="text-sm text-gray-400">No daily data.</p>
            ) : (
              <div className="space-y-1.5">
                {roomsSold.daily.map((d) => (
                  <div key={d.date} className="flex items-center gap-3 text-xs">
                    <span className="w-24 shrink-0 text-gray-500">{d.date}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-400 rounded"
                        style={{ width: `${(d.sold / maxSold) * 100}%` }}
                        aria-hidden="true"
                      />
                    </div>
                    <span className="w-8 shrink-0 text-right font-medium text-gray-700">{d.sold}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
